import React, { useState, useEffect } from 'react'
import { Info } from 'lucide-react'
import Modal from 'ui/Modal'
import InputField from 'ui/InputField'
import InputTextArea from 'ui/InputTextArea'
import { createTeam } from 'util/teamUtils'
import { toast } from 'sonner'
import api from 'config/axiosConfig'

interface Props {
    onClose: () => void
}

const TEAMNAME_REQUIREMENTS = {
    minLength: 2,
    maxLength: 30
};

const DISPLAY_NAME_REQUIREMENTS = {
    maxLength: 64
};

const BIO_REQUIREMENTS = {
    maxLength: 300
};

const RESTRICTED_TEAMNAME_LIST = ['team', 'societea', 'societea.io', 'societea.com', 'societea.app', 'www.societea.app', 'societeaapp', 'societeainc.com', 'societeainc', 'societea.inc', 'admin', 'administrator', 'partea', 'partea.io', 'partea.com', 'partea.app', 'deleted'];

const CreateTeamModal: React.FC<Props> = ({ onClose }) => {
    const [teamname, setTeamname] = useState<string>('');
    const [displayName, setDisplayName] = useState<string>('');
    const [isPrivate, setIsPrivate] = useState<boolean>(false);
    const [bioBrief, setBioBrief] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidTeamname, setIsValidTeamname] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string>('');

    const validateTeamname = (teamname: string): void => {
        setIsValidTeamname(true);
        setValidationMessage('');
        
        if (
            /^[a-z0-9_.]{2,30}$/.test(teamname) &&
            !/\.{2,}/.test(teamname) &&
            !teamname.startsWith(".") &&
            !teamname.startsWith("_") &&
            !teamname.endsWith(".") &&
            !teamname.endsWith("_") &&
            !teamname.includes("..") &&
            !teamname.includes("__") &&
            !/^admin/.test(teamname) &&
            !/admin$/.test(teamname) &&
            !RESTRICTED_TEAMNAME_LIST.includes(teamname)
        ) {
            setIsValidTeamname(true);
            setValidationMessage('');
        } else {
            if (teamname.startsWith(".") ||
                teamname.startsWith("_") ||
                teamname.endsWith(".") ||
                teamname.endsWith("_")
            ) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name must not start/end with '.' or '_'`);
            } else if (/^admin/.test(teamname) ||
                /admin$/.test(teamname) ||
                RESTRICTED_TEAMNAME_LIST.includes(teamname)
            ) {
                setIsValidTeamname(false);
                setValidationMessage('Partea name is not allowed.');
            } else if (!/^[a-z0-9_.]{2,30}$/.test(teamname)) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name may not contain special characters other than '.' and '_'`);
            } else if (teamname.includes("..") ||
                teamname.includes("__")
            ) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name may not contain consecutive '.' or '_'`);
            }
        }
    };

    useEffect(() => {
        if (teamname && teamname.length > 1) {
            validateTeamname(teamname);
        } else if (teamname && 
            (teamname.startsWith(".") ||
            teamname.startsWith("_"))
        ) {
            setIsValidTeamname(false);
            setValidationMessage(`Partea name must not start with '.' or '_'`);
        } else if (teamname && teamname.length < TEAMNAME_REQUIREMENTS.minLength) {
            setIsValidTeamname(false);
            setValidationMessage(`Partea name must be at least ${TEAMNAME_REQUIREMENTS.minLength} characters long.`);
        } else {
            setValidationMessage('');
        }
    }, [teamname]);

    const handleTeamnameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const lowercaseTeamname = e.target.value.toLowerCase();
        setTeamname(lowercaseTeamname);
    };

    const handleDisplayNameChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDisplayName(e.target.value);
    };

    const handlePrivateChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setIsPrivate(e.target.checked);
    };

    const handleBioBriefChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setBioBrief(e.target.value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            await createTeam(teamname, displayName, isPrivate, bioBrief);
            toast.success('Partea created successfully!');
            onClose();
        } catch (error: any) {
            try {
                const response = await api.post('/api/team', {
                    teamname,
                    display_name: displayName,
                    is_private: isPrivate,
                    bio_brief: bioBrief
                });
                return response;
            } catch (error) {
                console.error('Error creating Partea:', error);
                if ((error as any).response?.status === 409) {
                    setValidationMessage('Partea name already taken.');
                } else {
                    setError('Failed to create Partea. Please try again.');
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isButtonDisabled = (!teamname || !isValidTeamname) ||
        isSubmitting || 
        teamname.length < TEAMNAME_REQUIREMENTS.minLength;

    return (
        <Modal isOpen={true} onClose={onClose} title="New Partea">
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Parteas allow users to come together to build communities for analysis, solutioning, and activism. Creating Parteas for local and national issues, especially for moving from solutions to actions, is just one way to get started!
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        <div className="form-control">
                            <InputField
                                type="text"
                                id="teamname"
                                name="teamname"
                                labelText="Partea name"
                                fieldPlaceholder="Partea name"
                                value={teamname}
                                onChange={handleTeamnameChange}
                                validationState={isValidTeamname && teamname.length >= TEAMNAME_REQUIREMENTS.minLength ? 'valid' : teamname && !isValidTeamname ? 'invalid' : 'none'}
                                error={validationMessage}
                                spellCheck={false}
                                maxLength={TEAMNAME_REQUIREMENTS.maxLength}
                                minLength={TEAMNAME_REQUIREMENTS.minLength}
                                fieldRequired
                            />
                        </div>

                        <div className="form-control">
                            <InputTextArea
                                id="displayName"
                                name="displayName"
                                labelText="Display name"
                                fieldPlaceholder="Display name"
                                value={displayName}
                                onChange={handleDisplayNameChange}
                                maxLength={DISPLAY_NAME_REQUIREMENTS.maxLength}
                            />
                        </div>

                        <div className="form-control">
                            <div className="group flex justify-between items-center p-4 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                                <div className="flex flex-col items-start">
                                    <span className="text-eoe-text dark:text-eoe-dark-text">Private Partea</span>
                                    <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                                        Private Parteas require approval for followers.
                                    </p>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={isPrivate}
                                    onChange={handlePrivateChange}
                                    className="toggle-switch-input col-span-1 flex-shrink-0"
                                />
                            </div>
                        </div>

                        <div className="form-control">
                            <InputTextArea
                                id="bioBrief"
                                name="bioBrief"
                                labelText="Description"
                                fieldPlaceholder="Description"
                                value={bioBrief}
                                onChange={handleBioBriefChange}
                                maxLength={BIO_REQUIREMENTS.maxLength}
                            />
                        </div>

                        {error && (
                            <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                <Info className="mr-1 size-4" />
                                <span>{error}</span>
                            </div>
                        )}
                    </form>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isButtonDisabled}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSubmitting ? 'Creating...' : 'Create'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateTeamModal; 